/* =========================================================================
   SOHO — Story dock + fullscreen story player.
   Owner: sections/soho-story-dock.liquid, assets/soho-story-dock.css

   HARD RULES THIS FILE OBEYS
   --------------------------
   (7) It NEVER touches a <video> it does not own. There is no
       `document.querySelectorAll('video')` in this file and there never may
       be: that call would pause the "On the girls" rail on the nine live
       bikini-set landing pages and the home page background video. Every
       media query in here is rooted at `this` (the <soho-story-dock> element),
       and the only <video> that ever exists inside `this` is the one the
       player itself created. Nothing outside is paused, so there is nothing
       to resume on close — see `pauseOwnMedia()`.
   (11) No scroll listener at all, therefore no rAF-throttled scroll listener.
        The progress bar uses a plain rAF loop that runs ONLY while the player
        is open. Video is preload="none", always has a poster, and is
        muted+playsinline so autoplay is legal in every browser.
        prefers-reduced-motion disables autoplay and auto-advance.
   (1) This script never decides whether anything is visible on first paint.
       The dock is fully rendered and clickable by Liquid + CSS alone; JS only
       adds the player. If this file fails to load, the bubbles still show and
       still are focusable buttons (they just do nothing on click).
   (15) MULTI-PRODUCT SHOP BAR — docs/CONTRATO-SHOP-VIDEO.md §7.
       This file does NOT implement a product row. It asks
       `window.SohoShopVideo.mountTray(host, payload, {compact:true})` for one,
       exactly as sections/soho-video-reels.liquid's modal does, so the tray's
       stylesheet can never end up orphaned on one of the two sides. Nor does
       it parse the payload: `SohoShopVideo.readPayload(el)` owns every handle
       check, in one place, per the contract.
       Everything about the call is degradable. No `window.SohoShopVideo` (the
       asset 404s, a CSP blocks it, another script threw first), a payload the
       shared validator rejects, or a `mountTray` that throws — each of those
       returns early and leaves the single-product card Liquid rendered as the
       fallback, still linked, still priced, still with a Quick add. A dead
       shop bar is not one of the outcomes.
       One thing the tray CANNOT do for itself, and this file must: §3 says
       the host closes before the theme's cart events fire, because the cart
       drawer opens at z-index 6001 and this player sits at 2147482996. The
       modal closes itself; a tray inside somebody else's player calls the
       `onAdded` callback we hand it, and we close there.
   ========================================================================= */

(function () {
  'use strict';

  if (window.customElements && window.customElements.get('soho-story-dock')) return;

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function reducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  function clamp(n, lo, hi) {
    return n < lo ? lo : n > hi ? hi : n;
  }

  var SohoStoryDock = class SohoStoryDock extends HTMLElement {
    constructor() {
      super();
      this.index = -1;
      this.open = false;
      this.raf = 0;
      this.imgStart = 0;
      this.imgPaused = 0;
      this.lastFocus = null;
      this.prevOverflow = null; // (7) what <html> had before WE locked it
      // (15) set the moment a finger or the keyboard lands on the shop bar,
      // cleared on every slide change. See holdShopping().
      this.shopping = false;
      this.onKey = this.onKey.bind(this);
      this.tick = this.tick.bind(this);
    }

    connectedCallback() {
      if (this.booted) return;
      this.booted = true;

      // Everything below is queried inside `this`. Never inside `document`.
      // `allSlides` is every slide the section rendered, in Liquid order, and
      // the index on [data-soho-dock-open] indexes into THIS list forever.
      // `this.slides` is the subset that is live on this viewport right now —
      // see refreshSlides(). (5)
      this.allSlides = Array.prototype.slice.call(
        this.querySelectorAll('template[data-soho-dock-slide]')
      );
      this.slides = this.allSlides.slice();
      this.player = this.querySelector('[data-soho-dock-player]');
      this.stage = this.querySelector('[data-soho-dock-stage]');
      this.swipeArea = this.querySelector('[data-soho-dock-swipe]') || this.stage;
      this.bars = this.querySelector('[data-soho-dock-bars]');
      // (6) the permanent second row of the frame's flex column; the cloned
      // shop bar is moved into it on every slide. See the note in the section.
      this.buyBar = this.querySelector('[data-soho-dock-buy]');
      this.titleEl = this.querySelector('[data-soho-dock-title]');
      this.soundBtn = this.querySelector('[data-soho-dock-sound]');
      this.rail = this.querySelector('[data-soho-dock-rail]');

      if (!this.player || !this.stage || !this.allSlides.length) return;

      this.autoAdvance = this.getAttribute('data-autoadvance') !== 'false';
      this.imageMs = parseInt(this.getAttribute('data-image-ms'), 10) || 6000;
      this.storageKey = this.getAttribute('data-storage-key') || '';

      this.refreshSlides();
      this.buildBars();
      this.bind();
      this.restoreHidden();
      this.restoreSeen();
    }

    disconnectedCallback() {
      this.close(true);
      document.removeEventListener('keydown', this.onKey, true);
    }

    /* ------------------------------------------------------------- wiring */

    bind() {
      var self = this;

      this.addEventListener('click', function (evt) {
        // (15) The bag chip on a bubble that sells more than one thing. It is
        // a real <a href> to the first product -- never a `#`, never a dead
        // control -- but when the story has a whole look in it, opening the
        // player (and its tray) is the right answer, so we take the click.
        //
        // The index is deliberately NOT read off this anchor. It is read off
        // the <li>'s own bubble button, because that is the element
        // assets/soho-video-config.js renumbers when the dashboard replaces
        // the stories; a second copy of the index on the chip would go stale
        // and open somebody else's video.
        var shopper = evt.target.closest('[data-soho-dock-shop]');
        if (shopper && self.contains(shopper)) {
          var host = shopper.closest('.soho-dock__item');
          var owner = host ? host.querySelector('[data-soho-dock-open]') : null;
          if (owner) {
            evt.preventDefault();
            self.show(parseInt(owner.getAttribute('data-soho-dock-open'), 10), shopper);
            return;
          }
          // No button to take the index from: let the href do its job.
          return;
        }

        var opener = evt.target.closest('[data-soho-dock-open]');
        if (opener && self.contains(opener)) {
          evt.preventDefault();
          self.show(parseInt(opener.getAttribute('data-soho-dock-open'), 10), opener);
          return;
        }
        if (evt.target.closest('[data-soho-dock-close]')) {
          evt.preventDefault();
          self.close();
          return;
        }
        if (evt.target.closest('[data-soho-dock-next]')) {
          evt.preventDefault();
          self.step(1);
          return;
        }
        if (evt.target.closest('[data-soho-dock-prev]')) {
          evt.preventDefault();
          self.step(-1);
          return;
        }
        if (evt.target.closest('[data-soho-dock-sound]')) {
          evt.preventDefault();
          self.toggleSound();
          return;
        }
        if (evt.target.closest('[data-soho-dock-hide]')) {
          evt.preventDefault();
          self.hideRail();
        }
      });

      // Click the backdrop (outside the 9:16 frame) to close.
      this.player.addEventListener('click', function (evt) {
        if (evt.target === self.player) self.close();
      });

      // (15) A shopper reading the tray must not have the story yanked out
      // from under them. Both listeners are on the PERMANENT bar element, not
      // on the tray, so they survive every slide change and every remount.
      if (this.buyBar) {
        var hold = function () {
          self.shopping = true;
        };
        this.buyBar.addEventListener('pointerdown', hold, { passive: true });
        this.buyBar.addEventListener('focusin', hold);
      }

      this.bindSwipe();
    }

    bindSwipe() {
      var self = this;
      var area = this.swipeArea;
      var x0 = 0;
      var y0 = 0;
      var live = false;

      area.addEventListener(
        'touchstart',
        function (e) {
          if (e.touches.length !== 1) return;
          live = true;
          x0 = e.touches[0].clientX;
          y0 = e.touches[0].clientY;
          self.holdStart();
        },
        { passive: true }
      );

      area.addEventListener(
        'touchend',
        function (e) {
          if (!live) return;
          live = false;
          self.holdEnd();
          var t = e.changedTouches[0];
          var dx = t.clientX - x0;
          var dy = t.clientY - y0;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
            self.step(dx < 0 ? 1 : -1);
          } else if (dy > 70 && Math.abs(dy) > Math.abs(dx)) {
            self.close();
          }
        },
        { passive: true }
      );

      area.addEventListener(
        'touchcancel',
        function () {
          if (!live) return;
          live = false;
          self.holdEnd();
        },
        { passive: true }
      );
    }

    onKey(evt) {
      if (!this.open) return;
      if (evt.key === 'Escape') {
        evt.preventDefault();
        this.close();
      } else if (evt.key === 'ArrowRight') {
        evt.preventDefault();
        this.step(1);
      } else if (evt.key === 'ArrowLeft') {
        evt.preventDefault();
        this.step(-1);
      } else if (evt.key === ' ' || evt.key === 'Spacebar') {
        if (evt.target.closest('button, a')) return;
        evt.preventDefault();
        this.togglePlay();
      } else if (evt.key === 'Tab') {
        this.trap(evt);
      }
    }

    trap(evt) {
      var nodes = Array.prototype.filter.call(
        this.player.querySelectorAll(FOCUSABLE),
        function (el) {
          return el.offsetParent !== null || el === document.activeElement;
        }
      );
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      } else if (!this.player.contains(document.activeElement)) {
        evt.preventDefault();
        first.focus();
      }
    }

    /* ------------------------------------------------- device-aware slides */

    /**
     * (5) The Device select used to hide the BUBBLE with CSS while the SLIDE
     * stayed in the deck: a "Phone only" story still took a segment in the
     * progress bars on a desktop and still played when you pressed Next.
     *
     * The rule itself is not decided here. It is decided by
     * `.soho-dock__item--mobile` / `--desktop` in soho-story-dock.css, which
     * the Liquid puts on the <li>; this method just reads CSS's verdict, so
     * the 750px breakpoint is written down exactly once, in the stylesheet.
     *
     * getComputedStyle is deliberate rather than offsetParent: an element's
     * OWN computed `display` is reported correctly even when an ancestor is
     * display:none, so a dismissed rail (`rail.hidden`) or an editor redraw
     * cannot empty the deck by accident. It reads no geometry, so it forces
     * no layout.
     */
    refreshSlides() {
      var live = [];
      var unmeasurable = 0;
      for (var i = 0; i < this.allSlides.length; i++) {
        var tpl = this.allSlides[i];
        var li = tpl.closest ? tpl.closest('.soho-dock__item') : null;
        var on = true;
        if (li) {
          try {
            on = window.getComputedStyle(li).display !== 'none';
          } catch (e) {
            unmeasurable++;
          }
        } else {
          unmeasurable++;
        }
        if (on) live.push(tpl);
      }
      // An empty deck is a legitimate answer -- every bubble set to the other
      // device -- and in that case no bubble is on screen to open the player
      // anyway. Fall back to "everything" ONLY when we could not read the
      // styles at all, because guessing beats a dead player.
      this.slides = live.length || !unmeasurable ? live : this.allSlides.slice();
    }

    /* -------------------------------------------------------------- bars */

    buildBars() {
      if (!this.bars) return;
      this.bars.textContent = '';
      for (var i = 0; i < this.slides.length; i++) {
        var bar = document.createElement('div');
        bar.className = 'soho-dock__bar';
        var fill = document.createElement('span');
        bar.appendChild(fill);
        this.bars.appendChild(bar);
      }
      this.fills = Array.prototype.slice.call(this.bars.querySelectorAll('span'));
    }

    paintBars(pct) {
      if (!this.fills) return;
      for (var i = 0; i < this.fills.length; i++) {
        var v = i < this.index ? 100 : i > this.index ? 0 : clamp(pct, 0, 100);
        this.fills[i].style.width = v + '%';
      }
    }

    /* ------------------------------------------------------------ open/close */

    show(i, trigger) {
      if (isNaN(i) || i < 0 || i >= this.allSlides.length) return;

      // (5) Recompute the deck on every open, so a rotated phone or a resized
      // window is handled without a resize listener, then translate the
      // bubble's permanent Liquid index into a position in that deck.
      this.refreshSlides();
      var pos = this.slides.indexOf(this.allSlides[i]);
      if (pos < 0) return; // this bubble is not on this device
      this.buildBars();

      if (!this.open) {
        this.lastFocus = trigger || document.activeElement;
        this.player.hidden = false;
        this.open = true;
        // (1) BUG 1's FIX, the JS half. The CSS hides the rail on
        // `.soho-dock.is-open`, and THIS is what writes it. The old build
        // only reacted to `html[data-scroll-locked]` — an attribute the THEME
        // writes for ITS overlays. Our player never wrote it (it only sets
        // documentElement.style.overflow), so opening a story left the bubbles
        // painting on top of the fullscreen player on phones.
        // Never depend on someone else's attribute to hide something of ours.
        this.classList.add('is-open');
        // Belt and braces: even with the stylesheet missing, the rail stops
        // being focusable and stops taking pointer events while the story is up.
        if (this.rail) this.rail.setAttribute('inert', '');
        // (7) Never blind-reset. Remember what <html> had — the theme's own
        // scroll lock, or a merchant's inline style — and put that back.
        this.prevOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onKey, true);
      }
      this.render(pos);
    }

    close(silent) {
      if (!this.open) return;
      this.open = false;
      this.stopTick();
      this.pauseOwnMedia();
      this.stage.textContent = ''; // destroys our <video>, releasing the fetch
      if (this.buyBar) this.buyBar.textContent = '';
      this.player.hidden = true;
      // (1) release the eclipse: the bubbles come back the moment the story
      // closes. Paired with the classList.add/setAttribute in show().
      this.classList.remove('is-open');
      if (this.rail) this.rail.removeAttribute('inert');
      // (7) restore, do not blank.
      document.documentElement.style.overflow = this.prevOverflow || '';
      this.prevOverflow = null;
      document.removeEventListener('keydown', this.onKey, true);
      this.index = -1;
      this.paintBars(0);
      if (!silent && this.lastFocus && document.contains(this.lastFocus)) {
        try {
          this.lastFocus.focus();
        } catch (e) {
          /* noop */
        }
      }
      this.lastFocus = null;
    }

    step(dir) {
      var next = this.index + dir;
      if (next < 0) {
        this.render(0);
        return;
      }
      if (next >= this.slides.length) {
        this.close();
        return;
      }
      this.render(next);
    }

    /* ---------------------------------------------------------- rendering */

    render(i) {
      this.stopTick();
      this.pauseOwnMedia();
      this.index = i;
      // (15) A new story is a new decision: the previous slide's "they are
      // busy buying" hold does not carry over.
      this.shopping = false;

      var tpl = this.slides[i];
      // (3) "Already watched" is marked HERE rather than in show(), so a story
      // reached by Next, by a swipe or by auto-advance counts exactly the same
      // as one that was tapped. `slides` is the device-filtered deck, so the
      // position has to be translated back into the permanent Liquid index
      // that the bubble's data attribute uses.
      this.markSeen(this.allSlides.indexOf(tpl));
      this.stage.textContent = '';
      if (this.buyBar) this.buyBar.textContent = '';
      this.stage.appendChild(tpl.content.cloneNode(true));

      // (6) Lift the shop bar out of the absolutely positioned slide layer and
      // into the frame's flex column, where it is a real row under the stage.
      // A slide with no product leaves the row empty, and an empty row with no
      // padding of its own is 0px tall -- the stage simply keeps the space.
      if (this.buyBar) {
        var buy = this.stage.querySelector('.soho-dock__buy');
        if (buy) this.buyBar.appendChild(buy);
        this.mountShop(tpl, buy);
      }

      if (this.titleEl) this.titleEl.textContent = tpl.getAttribute('data-title') || '';

      // Scoped to the stage — never to the document. (correction 7)
      this.video = this.stage.querySelector('video');
      this.paintBars(0);

      if (this.soundBtn) this.soundBtn.hidden = !this.video;

      if (this.video) {
        var self = this;
        this.video.muted = this.muted !== false;
        this.video.playsInline = true;
        this.video.setAttribute('playsinline', '');
        this.video.preload = 'auto'; // only now, and only for this one element
        this.video.addEventListener('ended', function () {
          // (15) `shopping` vetoes the JUMP, never the playback and never the
          // progress bar: a shopper who has just tapped a product card gets to
          // finish the thought instead of being thrown into the next story.
          // They still have Next, the swipe and the arrow keys.
          if (self.autoAdvance && !reducedMotion() && !self.shopping) self.step(1);
          else self.paintBars(100);
        });
        this.video.addEventListener('error', function () {
          // A dead source must not strand the viewer on a black frame.
          self.imgStart = Date.now();
          self.startTick();
        });
        if (!reducedMotion()) {
          var p = this.video.play();
          if (p && typeof p.catch === 'function') {
            p.catch(function () {
              /* autoplay refused: the poster stays, the bar still runs */
            });
          }
        }
        this.startTick();
      } else {
        this.imgStart = Date.now();
        this.imgPaused = 0;
        if (!reducedMotion()) this.startTick();
        else this.paintBars(0);
      }

      var focusTarget = this.player.querySelector('[data-soho-dock-close]');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    /* ------------------------------------------------------- the shop bar */

    /**
     * (15) Give the story the swipeable product strip, when there is one to
     * give. Two situations ask for it, and they arrive by different routes:
     *
     *   · a THEME story that sells two or more things — Liquid rendered the
     *     bar with `data-soho-dock-tray` on it, plus a single-product card
     *     inside it as the fallback;
     *   · a DASHBOARD story — assets/soho-video-config.js stopped cloning the
     *     shop bar altogether and writes only the payload, on purpose, so
     *     that a store whose theme blocks never had a product still sells the
     *     whole outfit. There is no bar in the clone, so we build one.
     *
     * A theme story with exactly ONE destination is neither: its bar carries
     * no `data-soho-dock-tray` and we leave it completely alone. A one-card
     * strip would be a worse version of a bar that already works.
     *
     * WHERE THE PAYLOAD COMES FROM: `readPayload(li)`, the <li> itself.
     * Never the stage. `soho-shop-modal.js` caches one parsed payload per
     * ELEMENT, and the stage is a single element reused by every slide —
     * handing it the stage would serve the first story's products for the
     * rest of the visit. The <li> is unique per story, so the cache is right
     * by construction. Parsing and handle validation stay entirely inside
     * readPayload, per contract §7: there is no JSON.parse in this file.
     *
     * ORDER OF OPERATIONS. The fallback card is removed only AFTER mountTray
     * has returned a tray, and the tray is mounted into a host div of our
     * own, so that a mountTray which throws half-way leaves exactly one node
     * to take back out. A bar we created ourselves and could not fill is
     * removed too — an empty cream strip under the video is worse than none.
     */
    mountShop(tpl, buy) {
      if (!this.buyBar) return;

      // A theme bar with one destination is finished as it is.
      var wanted = buy ? buy.getAttribute('data-soho-dock-tray') !== null : true;
      if (!wanted) return;

      var api = window.SohoShopVideo;
      if (!api || typeof api.mountTray !== 'function' || typeof api.readPayload !== 'function') {
        return; // asset not on the page: whatever Liquid rendered stays
      }

      var li = tpl && tpl.closest ? tpl.closest('.soho-dock__item') : null;
      if (!li) return;

      var payload = null;
      try {
        payload = api.readPayload(li);
      } catch (e) {
        payload = null;
      }
      if (!payload) return;

      var bar = buy;
      if (!bar) {
        // The cream ground, the gold hairline and the safe-area padding all
        // live on `.soho-dock__buy`, not on the permanent row, so a bar built
        // here has to be that same class to look like the one Liquid writes.
        bar = document.createElement('div');
        bar.className = 'soho-dock__buy';
        this.buyBar.appendChild(bar);
      }

      var fallback = bar.querySelector('.soho-dock__buy-card');
      var host = document.createElement('div');
      host.className = 'soho-dock__tray-host';
      bar.appendChild(host);

      var self = this;
      var tray = null;
      try {
        tray = api.mountTray(host, payload, {
          compact: true,
          /* CONTRACT §3, and it is not optional here. The theme's cart drawer
             opens at z-index 6001 and this player sits at 2147482996, so a
             player still open when the drawer arrives hides the one piece of
             feedback that says the bikini went in. The modal closes ITSELF
             for the same reason; a tray cannot, so it calls this first and
             fires the theme's two events after we return.
             `close()` is idempotent and restores focus to the bubble that
             opened the story, which is where the shopper came from. */
          onAdded: function () {
            self.close();
          }
        });
      } catch (e) {
        tray = null;
      }

      if (!tray) {
        if (host.parentNode) host.parentNode.removeChild(host);
        if (!buy && bar.parentNode) bar.parentNode.removeChild(bar);
        return;
      }

      if (fallback && fallback.parentNode) fallback.parentNode.removeChild(fallback);
      // The bar stops being a centred flex row and becomes a plain block whose
      // only child is the tray. Written here, read in soho-story-dock.css.
      bar.classList.add('is-tray');
    }

    /* ------------------------------------------------------------- ticking */

    startTick() {
      this.stopTick();
      this.raf = window.requestAnimationFrame(this.tick);
    }

    stopTick() {
      if (this.raf) window.cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    tick() {
      if (!this.open) return;
      var pct = 0;
      if (this.video && this.video.duration && isFinite(this.video.duration)) {
        pct = (this.video.currentTime / this.video.duration) * 100;
      } else if (!this.video) {
        var elapsed = Date.now() - this.imgStart;
        pct = (elapsed / this.imageMs) * 100;
        if (pct >= 100) {
          this.paintBars(100);
          if (this.autoAdvance && !reducedMotion() && !this.shopping) {
            this.step(1);
            return;
          }
          this.stopTick();
          return;
        }
      }
      this.paintBars(pct);
      this.raf = window.requestAnimationFrame(this.tick);
    }

    holdStart() {
      if (this.video && !this.video.paused) {
        this.video.pause();
        this.held = true;
      } else if (!this.video) {
        this.imgPaused = Date.now();
        this.held = true;
      }
    }

    holdEnd() {
      if (!this.held) return;
      this.held = false;
      if (this.video) {
        var p = this.video.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      } else if (this.imgPaused) {
        this.imgStart += Date.now() - this.imgPaused;
        this.imgPaused = 0;
      }
      // The rAF loop stops itself when a photo slide finishes or is held.
      // Restart it so the progress bar keeps moving after the finger lifts.
      if (this.open) this.startTick();
    }

    togglePlay() {
      if (!this.video) return;
      if (this.video.paused) {
        var p = this.video.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
        this.startTick();
      } else {
        this.video.pause();
        this.stopTick();
      }
    }

    toggleSound() {
      if (!this.video) return;
      this.video.muted = !this.video.muted;
      this.muted = this.video.muted; // remembered for the next slide
      if (this.soundBtn) {
        this.soundBtn.setAttribute('aria-pressed', this.video.muted ? 'false' : 'true');
        var on = this.soundBtn.querySelector('[data-sound-on]');
        var off = this.soundBtn.querySelector('[data-sound-off]');
        if (on) on.hidden = this.video.muted;
        if (off) off.hidden = !this.video.muted;
      }
    }

    /**
     * Pause ONLY the media this element created. This is the whole of
     * correction 7: the selector is rooted at `this.stage`, so the "On the
     * girls" rail on the set pages and the home hero video are untouched and
     * therefore need no resuming.
     */
    pauseOwnMedia() {
      var mine = this.stage ? this.stage.querySelectorAll('video') : [];
      for (var i = 0; i < mine.length; i++) {
        try {
          mine[i].pause();
        } catch (e) {
          /* noop */
        }
      }
      this.video = null;
    }

    /* ------------------------------------------------------- hide the rail */

    hideRail() {
      if (this.rail) this.rail.hidden = true;
      if (!this.storageKey) return;
      try {
        window.sessionStorage.setItem(this.storageKey, '1');
      } catch (e) {
        /* private mode */
      }
    }

    /* --------------------------------------------------- already watched (3) */

    /**
     * Kept in sessionStorage, in one key beside the dismiss key, for the same
     * reason the dismiss state is: a story is "new" again on the shopper's
     * next visit, but it should not go bright again just because they clicked
     * through to a product and came back. The value is the bubble's Liquid
     * index, which is stable for as long as the block order is.
     */
    seenKey() {
      return this.storageKey ? this.storageKey + ':seen' : '';
    }

    readSeen() {
      var out = {};
      var key = this.seenKey();
      if (!key) return out;
      try {
        var raw = window.sessionStorage.getItem(key) || '';
        var parts = raw.split(',');
        for (var i = 0; i < parts.length; i++) {
          if (parts[i] !== '') out[parts[i]] = 1;
        }
      } catch (e) {
        /* private mode */
      }
      return out;
    }

    /** Scoped to `this`, like every other query in this file. */
    itemAt(i) {
      if (isNaN(i) || i < 0) return null;
      var btn = this.querySelector('[data-soho-dock-open="' + i + '"]');
      return btn && btn.closest ? btn.closest('.soho-dock__item') : null;
    }

    markSeen(i) {
      if (isNaN(i) || i < 0) return;
      var li = this.itemAt(i);
      if (li) li.classList.add('is-seen');
      var key = this.seenKey();
      if (!key) return;
      try {
        var seen = this.readSeen();
        seen[String(i)] = 1;
        window.sessionStorage.setItem(key, Object.keys(seen).join(','));
      } catch (e) {
        /* private mode: the ring simply stays bright */
      }
    }

    restoreSeen() {
      // In the theme editor every ring stays bright, so Dylan is always
      // looking at the design he is editing and not at a dimmed leftover.
      if (window.Shopify && window.Shopify.designMode) return;
      var seen = this.readSeen();
      for (var k in seen) {
        if (!Object.prototype.hasOwnProperty.call(seen, k)) continue;
        var li = this.itemAt(parseInt(k, 10));
        if (li) li.classList.add('is-seen');
      }
    }

    restoreHidden() {
      if (!this.storageKey || !this.rail) return;
      if (window.Shopify && window.Shopify.designMode) return; // always visible in the editor
      try {
        if (window.sessionStorage.getItem(this.storageKey) === '1') this.rail.hidden = true;
      } catch (e) {
        /* private mode */
      }
    }
  };

  window.customElements.define('soho-story-dock', SohoStoryDock);
})();
